/**
 * CHI ASPETTA UN TECNICO (25/09/2026, riunione con Umberto del 24/09).
 *
 * La postazione del banco squilla finche' qualcuno non preme «Rispondo io».
 * Questo file decide, dalle righe del banco, QUALI fili stanno aspettando:
 * nessun suono qui, solo la regola, cosi' si prova da sola (squillo.test.ts).
 *
 * Un filo aspetta quando, nella richiesta in corso, il cliente ha scritto (o
 * il salvagente gli ha aperto la porta con la scheda di contesto) e dal banco
 * non e' ancora arrivato niente: ne' un «Rispondo io» ne' una risposta.
 *
 * La RICHIESTA IN CORSO parte dall'ultima chiusura del caso o dall'ultima
 * scheda di contesto (una richiesta nuova: il salvagente, o il cliente che
 * passa dall'assistente al banco), se viene dopo. E' lo stesso confine che
 * usa prendi_filo() nel database (migrazione 20260925100000): se i due non
 * coincidessero, «Rispondo io» direbbe «gia' preso» e lo squillo non
 * smetterebbe piu'.
 *
 * Solo le richieste degli ultimi tre giorni squillano (un venerdi' sera
 * arriva al lunedi' mattina): le prove vecchie rimaste senza risposta non
 * fanno suonare la postazione il giorno in cui la si apre.
 */

export interface RigaBanco {
  user_id: string;
  autore_id?: string;
  da_banco: boolean;
  contesto: boolean;
  chiusura: boolean;
  presa?: boolean | null;
  nome_autore?: string | null;
  testo: string;
  created_at: string;
}

export const FINESTRA_SQUILLO_ORE = 72;

/**
 * CHI SQUILLA: L'ASSISTENZA TECNICA (incontro in sede del 02/11/2026,
 * Davide il 03/11: «luca, leonardo, assistenza sono i 3 account»). Risponde il
 * primo che e' libero. Fino al 02/11 squillava solo supporto@, il PC fisso del
 * banco (Davide, 28/09). Gli altri account interni aprono la postazione,
 * vedono chi aspetta e rispondono, ma in silenzio. La stessa lista e' in
 * supabase/functions/_shared/squillo_banco.ts (i telefoni a cui va la push).
 */
export const ACCOUNT_CHE_SQUILLANO = [
  "l.micheli@datacomtecnologie.it",
  "l.biagioli@datacomtecnologie.it",
  "assistenza@datacomtecnologie.it",
];

/**
 * Il canale Android dello squillo sui telefoni (06/10/2026): lo stesso nome in
 * supabase/functions/_shared/squillo_banco.ts, che lo chiede nelle notifiche.
 */
export const CANALE_SQUILLO = "banco_squillo";

export function squillaQui(email: string | null | undefined): boolean {
  return ACCOUNT_CHE_SQUILLANO.includes((email ?? "").trim().toLowerCase());
}

export interface Attesa {
  user_id: string;
  /** Da quando aspetta: la prima riga del cliente nella richiesta. */
  dal: string;
  /** L'ultima riga del cliente (anteprima). */
  ultima: RigaBanco;
}

export interface Presa {
  user_id: string;
  da?: string;
  nome: string | null;
  il: string;
}

/** Le righe della richiesta in corso di UN filo (righe in ordine di tempo). */
export function richiestaInCorso(righe: RigaBanco[]): RigaBanco[] {
  let inizio = 0;
  for (let i = 0; i < righe.length; i++) {
    const r = righe[i];
    if (r.chiusura) inizio = i + 1;
    else if (r.contesto && !r.da_banco) inizio = i;
  }
  return righe.slice(inizio);
}

export interface StatoFili {
  inAttesa: Attesa[];
  prese: Map<string, Presa>;
}

export function statoFili(
  righe: RigaBanco[],
  adesso: number = Date.now(),
  finestraOre: number = FINESTRA_SQUILLO_ORE,
): StatoFili {
  const perFilo = new Map<string, RigaBanco[]>();
  for (const r of righe) {
    const lista = perFilo.get(r.user_id) ?? [];
    lista.push(r);
    perFilo.set(r.user_id, lista);
  }

  const inAttesa: Attesa[] = [];
  const prese = new Map<string, Presa>();
  const limite = adesso - finestraOre * 3_600_000;

  for (const [uid, lista] of perFilo) {
    lista.sort((a, b) => a.created_at.localeCompare(b.created_at));
    const richiesta = richiestaInCorso(lista);
    const presa = richiesta.find((r) => r.presa);
    if (presa) {
      prese.set(uid, { user_id: uid, da: presa.autore_id, nome: presa.nome_autore ?? null, il: presa.created_at });
    }
    const dalBanco = richiesta.some((r) => r.da_banco && !r.chiusura);
    const delCliente = richiesta.filter((r) => !r.da_banco);
    if (dalBanco || delCliente.length === 0) continue;
    const ultima = delCliente[delCliente.length - 1];
    if (new Date(ultima.created_at).getTime() < limite) continue;
    inAttesa.push({ user_id: uid, dal: delCliente[0].created_at, ultima });
  }

  // Chi aspetta da piu' tempo, per primo.
  inAttesa.sort((a, b) => a.dal.localeCompare(b.dal));
  return { inAttesa, prese };
}

/** «da 3 min», «da 1 h 20 min»: quanto aspetta, detto come a voce. */
export function daQuanto(dal: string, adesso: number = Date.now()): string {
  const minuti = Math.max(0, Math.floor((adesso - new Date(dal).getTime()) / 60_000));
  if (minuti < 1) return "da meno di un minuto";
  if (minuti < 60) return `da ${minuti} min`;
  const ore = Math.floor(minuti / 60);
  if (ore < 24) return minuti % 60 ? `da ${ore} h ${minuti % 60} min` : `da ${ore} h`;
  const giorni = Math.floor(ore / 24);
  return giorni === 1 ? "da ieri" : `da ${giorni} giorni`;
}
